import {LoadingButton} from '@mui/lab';
import {Box, Button} from '@mui/material';
import {useFormik} from 'formik';
import {useSnackbar} from 'notistack';
import {useMemo, useState} from 'react';

import API, {getMessagesFromApiError} from '../../api/axios';
import {apiBaseUrl} from '../../api/urls';
import {useAppDispatch, useAppSelector} from '../../hooks/redux';
import {
  CommtracNode,
  CommutracNodeMinerReassignInputBody,
} from '../../interfaces/CommtracNode';
import reduxActions from '../../redux/actions';
import {commtracNodeMinerReassignInputSchema} from '../../scheme/yup/commtrac-node';
import AssetHumanSelect from '../asset-human/AssetHumanSelect';
import {CloseSnackbarAction} from '../common/CloseSnackbarButton';
import SnackbarMessages from '../common/SnackbarMessages';

interface Props {
  item: CommtracNode;
  onCancel?: () => void;
  onSubmitted?: (item: CommtracNode) => void;
}

type ReassignInputBody = CommutracNodeMinerReassignInputBody;

// eslint-disable-next-line complexity
const CommtracNodeItemMinerReassign = ({
  item,
  onCancel,
  onSubmitted,
}: Props) => {
  const reduxDispatch = useAppDispatch();

  /**********/
  /* submit */
  /**********/

  const {enqueueSnackbar} = useSnackbar();
  const [submittedInProgress, setSubmittedInProgress] = useState(false);

  const submitData = async (data: ReassignInputBody) => {
    setSubmittedInProgress(true);
    try {
      const endpoint = `${apiBaseUrl}/commtrac-node/${item.id}/miner/reassign`;
      const resp = await API.patch<CommtracNode>(endpoint, data);
      const message = `Commtrac node has been reassigned`;
      enqueueSnackbar(message, {
        variant: 'success',
        action: CloseSnackbarAction,
      });
      onSubmitted?.(resp.data);
      reduxDispatch(reduxActions.assets.fetchAssetHumans);
      reduxDispatch(reduxActions.assets.fetchCommtracNodes);
    } catch (error: any) {
      const messages = getMessagesFromApiError(error, "STRA_0dc3b013");
      enqueueSnackbar(<SnackbarMessages messages={messages} />, {
        variant: 'error',
        action: CloseSnackbarAction,
      });
    }
    setSubmittedInProgress(false);
  };

  /*********/
  /* input */
  /*********/

  const assetHumansSlice = useAppSelector(({assets}) => assets.asset_humans);
  const assetHumans = useMemo(() => assetHumansSlice, [assetHumansSlice]);

  const getFormikValues = (item: CommtracNode): ReassignInputBody => ({
    asset_human_id: item.employee_asset_id,
  });

  const formik = useFormik<ReassignInputBody>({
    initialValues: getFormikValues(item),
    validationSchema: commtracNodeMinerReassignInputSchema,
    onSubmit: async (values) => {
      await submitData(values);
    },
  });

  return (
    <Box
      component="form"
      display="flex"
      flexDirection="column"
      position="relative"
      gap={3}
      onSubmit={formik.handleSubmit}
    >
      <Box display="flex" flexDirection="column" gap={3}>
        <AssetHumanSelect
          value={formik.values.asset_human_id}
          size="small"
          label="Select an Employee"
          fullWidth
          nullLabel=""
          assetHumans={assetHumans}
          error={
            !!formik.touched.asset_human_id && !!formik.errors.asset_human_id
          }
          onChange={(_, item) => {
            formik.setFieldValue('asset_human_id', item?.id ?? null);
          }}
        />
      </Box>

      <Box display="flex" justifyContent="space-between">
        <Box display="flex" justifyContent="end" gap={1}>
          {onCancel ? <Button onClick={() => onCancel()}>Cancel</Button> : null}
          <Box>
            <LoadingButton
              variant="contained"
              type="submit"
              loading={submittedInProgress}
              disabled={!formik.values.asset_human_id}
            >
              Reassign
            </LoadingButton>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default CommtracNodeItemMinerReassign;
